import React, { useMemo, useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MetricCard from './MetricCard';
import QueueChart from './QueueChart';
import AlertsList from './AlertsList';
import { TENANT_ID, API_BASE_URL } from '../utils/api';
import { useNotifications } from '../contexts/useNotifications';
import { useToast } from '../contexts/ToastContext';
import { Link } from 'react-router-dom';

const Overview: React.FC = () => {
  const [overview, setOverview] = useState<any>(null);
  const [queues, setQueues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const { connected: rtConnected, notifications } = useNotifications();
  const { addToast } = useToast();

  useEffect(() => {
    fetchOverview();
  }, []);

  // Derivar últimos eventos de jobs desde Socket.IO
  const jobEvents = useMemo(() => notifications.filter(n => n.type?.startsWith('job:')).slice(0, 8), [notifications]);

  useEffect(() => {
    const last = jobEvents[0];
    if (last) {
      const q = last.payload?.queueName || 'queue';
      const id = last.payload?.jobId || '';
      addToast({
        type: last.type === 'job:failed' ? 'error' : 'info',
        title: 'Evento de Job',
        message: `${last.type} · ${q} ${id ? `#${id}` : ''}`,
        duration: 2500,
      });
    }
  }, [jobEvents.length]);

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/monitoring/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        },
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
        // Derive queues from overview metrics
        const derivedQueues = (data.queueMetrics || []).map((m: any) => ({
          name: m.queueName || m.name,
          status: (m.workersActive && m.workersActive > 0) ? 'active' : 'inactive',
          active: m.active,
          waiting: m.waiting,
          completed: m.completed,
          failed: m.failed,
          throughputPerMinute: m.throughputPerMinute,
          concurrency: m.concurrency,
        }));
        setQueues(derivedQueues);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando métricas y datos de la plataforma...</p>
      </div>
    );
  }

  const bs = overview?.businessStats || {
    tenantsCount: 0,
    conversationsCount: 0,
    messagesCount: 0,
    productsCount: 0,
    ordersCount: 0,
    channelsCount: 0,
    connectedChannelsCount: 0,
    tenants: [],
    recentConversations: [],
    channelInstances: [],
  };

  return (
    <div className="overview-page">
      <div className="page-header">
        <div>
          <h2>📊 Panel General de Operaciones</h2>
          <p className="page-subtitle" style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '4px' }}>
            Estado operativo, actividad de clientes, canales y cola de mensajería en tiempo real.
          </p>
        </div>
        <div className="refresh-indicator">
          <button onClick={fetchOverview} className="refresh-button">
            🔄 Actualizar
          </button>
          <span className={`socket-indicator ${rtConnected ? 'on' : 'off'}`} title="Socket.IO Realtime">
            {rtConnected ? '🟢 En Vivo (Socket.IO)' : '🔴 Desconectado'}
          </span>
        </div>
      </div>

      {/* SECCIÓN 1: Métricas de Negocio y Plataforma */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🏢 Actividad Comercial & Canales (Base de Datos Real)
        </h3>
        <div className="metrics-grid">
          <MetricCard
            title="Tenants Registrados"
            value={bs.tenantsCount}
            icon="🏢"
            trend={`${bs.tenants.length} activos recientemente`}
          />
          <MetricCard
            title="Canales Activos"
            value={`${bs.connectedChannelsCount} / ${bs.channelsCount}`}
            icon="📱"
            type={bs.connectedChannelsCount > 0 ? 'success' : 'warning'}
            trend={`${bs.connectedChannelsCount} conectados`}
          />
          <MetricCard
            title="Conversaciones Totales"
            value={bs.conversationsCount}
            icon="💬"
            trend="Sesiones registradas"
          />
          <MetricCard
            title="Mensajes Procesados"
            value={bs.messagesCount}
            icon="✉️"
            trend={`${bs.productsCount} productos en catálogo`}
          />
        </div>
      </div>

      {/* SECCIÓN 2: Métricas del Worker / BullMQ */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          ⚡ Estado del Motor Asíncrono (BullMQ & Redis)
        </h3>
        <div className="metrics-grid">
          <MetricCard
            title="Jobs Activos"
            value={overview?.totalActiveJobs || 0}
            icon="⚡"
            trend={overview?.activeJobsTrend}
          />
          <MetricCard
            title="Jobs Completados"
            value={overview?.totalCompletedJobs || 0}
            icon="✅"
            trend={overview?.completedJobsTrend}
          />
          <MetricCard
            title="Jobs Fallidos"
            value={overview?.totalFailedJobs || 0}
            icon="❌"
            trend={overview?.failedJobsTrend}
            type={overview?.totalFailedJobs > 0 ? 'error' : undefined}
          />
          <MetricCard
            title="Throughput / min"
            value={overview?.throughputPerMinute || 0}
            icon="📈"
            trend={overview?.throughputTrend}
          />
        </div>
      </div>

      {/* SECCIÓN 3: Canales y Tenants Recientes */}
      <div className="charts-section" style={{ marginBottom: '2rem' }}>
        {/* Canales de Comunicación */}
        <div className="chart-container" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>📡 Canales Configurados</h3>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total: {bs.channelsCount}</span>
          </div>
          {bs.channelInstances.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.9rem' }}>No hay canales registrados aún.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {bs.channelInstances.map((ci: any) => (
                <div key={ci.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{ci.channelType === 'WHATSAPP' ? '🟢 WhatsApp' : ci.channelType === 'WEBCHAT' ? '💬 WebChat' : '✈️ Telegram'}</span>
                      <span style={{ color: 'var(--text-secondary)', fontWeight: 400, fontSize: '0.8rem' }}>({ci.instanceName || 'Sin nombre'})</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      Tenant: <strong>{ci.tenant?.name || 'Global'}</strong>
                    </div>
                  </div>
                  <div>
                    <span style={{
                      padding: '3px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      background: ci.status === 'CONNECTED' ? '#dcfce7' : ci.status === 'PENDING' ? '#fef9c3' : '#fee2e2',
                      color: ci.status === 'CONNECTED' ? '#166534' : ci.status === 'PENDING' ? '#854d0e' : '#991b1b'
                    }}>
                      {ci.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tenants / Empresas Registradas */}
        <div className="queues-status" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem' }}>🏢 Tenants del SaaS</h3>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Total: {bs.tenantsCount}</span>
          </div>
          {bs.tenants.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.9rem' }}>No hay empresas registradas.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {bs.tenants.map((t: any) => (
                <div key={t.id} style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>{t.name}</strong>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>/{t.slug}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    <span>💬 {t._count?.conversations || 0} chats</span>
                    <span>📦 {t._count?.products || 0} prods</span>
                    <span>🔌 {t._count?.channelInstances || 0} canales</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* SECCIÓN 4: Gráficos de Colas y Estado BullMQ */}
      <div className="charts-section">
        <div className="chart-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>📊 Throughput por Cola</h3>
            <Link to="/admin/jobs" style={{ fontSize: '0.85rem', color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 500 }}>
              Ver todos los Jobs →
            </Link>
          </div>
          <QueueChart queues={queues} />
        </div>
        
        <div className="queues-status">
          <h3>🔧 Estado de Colas</h3>
          <div className="queues-list">
            {queues.map((queue) => (
              <div key={queue.name} className="queue-item" style={{ position: 'relative' }}>
                <div className="queue-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Link
                      to={`/admin/queue/${queue.name}`}
                      style={{ fontWeight: 600, color: 'var(--primary-color)', textDecoration: 'none' }}
                    >
                      {queue.name}
                    </Link>
                    {queue.name === 'incoming-messages' && (
                      <Link
                        to={`/admin/queue/${queue.name}`}
                        style={{
                          fontSize: '0.75rem',
                          background: '#eff6ff',
                          color: '#2563eb',
                          border: '1px solid #bfdbfe',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                        title="Haga clic para ajustar la concurrencia de workers"
                      >
                        ⚡ Concurrencia: {queue.concurrency ?? 30}
                      </Link>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`queue-status ${queue.status}`}>
                      {queue.status === 'active' ? '🟢' : '🔴'} {queue.status}
                    </span>
                    <Link
                      to={`/admin/queue/${queue.name}`}
                      style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textDecoration: 'none' }}
                    >
                      Configurar →
                    </Link>
                  </div>
                </div>
                <div className="queue-metrics">
                  <span>Activos: {queue.active || 0}</span>
                  <span>Esperando: {queue.waiting || 0}</span>
                  <span>Completados: {queue.completed || 0}</span>
                  <span>Fallidos: {queue.failed || 0}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SECCIÓN 5: Conversaciones Recientes y Eventos de Jobs */}
      <div className="charts-section" style={{ marginTop: '1.5rem' }}>
        <div className="chart-container" style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>💬 Conversaciones Recientes</h3>
          {bs.recentConversations.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.9rem' }}>Sin conversaciones registradas.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {bs.recentConversations.map((c: any) => (
                <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.8rem', background: '#f8fafc', borderRadius: '6px', fontSize: '0.85rem' }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{c.contactName || c.waContact || 'Visitante'}</span>
                    <span style={{ color: 'var(--text-secondary)', marginLeft: '8px', fontSize: '0.78rem' }}>({c.tenant?.name})</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>✉️ {c._count?.messages || 0}</span>
                    <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '0.7rem', background: c.status === 'open' ? '#dcfce7' : '#f1f5f9', color: c.status === 'open' ? '#166534' : '#475569' }}>
                      {c.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Eventos recientes de Jobs */}
        <div className="job-events-section" style={{ margin: 0 }}>
          <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>⚙️ Eventos recientes de Jobs (Socket.IO)</h3>
          {jobEvents.length === 0 ? (
            <p className="muted" style={{ fontSize: '0.9rem' }}>Sin eventos recientes en la cola.</p>
          ) : (
            <ul className="job-events-list">
              {jobEvents.map((ev, idx) => (
                <li key={ev.id ?? `${ev.type}-${ev.payload?.jobId ?? idx}`} className={`job-event ${ev.type === 'job:failed' ? 'error' : ''}`}>
                  <span className="job-event-type">{ev.type}</span>
                  <span className="job-event-queue">{ev.payload?.queueName}</span>
                  {ev.payload?.jobId && <span className="job-event-id">#{ev.payload.jobId}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Alertas recientes */}
      <div className="alerts-section" style={{ marginTop: '1.5rem' }}>
        <h3>🚨 Alertas del Sistema</h3>
        <AlertsList alerts={overview?.activeAlerts || []} />
      </div>
    </div>
  );
};

export default Overview;

